import { ID } from "@nova/constants"
import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import {
    pdfRasterImageData,
    updatePDFRasterVisibility,
} from "features/entity-caching/pdf-rasters/thunks"
import {
    addEntitySocket,
    handleIncomingSocketEvents,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { loadProject } from "features/map/control/thunks"
import { RootState } from "integrations/redux"
import { memoize } from "nodash"
import { addLayerVisibilityToEntities } from "panels/content/side-panel/module"

type RasterById = Record<
    ID,
    {
        id: string
        /**
         * URL will only exist if it's a blob. API URL will be
         * computed when the visibility is toggled on for the
         * first time.
         *
         * This property refers to the canvas blob.
         */
        url?: string

        /**
         * Blob URL of the actual PDF. This will exist only when
         * an uploaded PDF is set to visible for the first time.
         * Used for exporting so that the already-downloaded PDF
         * doesn't need to be downloaded a second time.
         */
        pdfBlobUrl?: string
        visible: boolean
        isDownloading: boolean
    }
>

type UIState = {
    /**
     * This only holds local properties that live on this client
     * only, and outside of Protobuf. This includes the url
     * (which can be a blob url), the visibility (which is disabled
     * by default on every refresh), and the PDF downloading status.
     */
    rasterById: RasterById
}

const uiStateName = "pdf-raster-ui-v1"
type uiStateNameType = typeof uiStateName

const initialState: UIState = {
    rasterById: {},
}

function isValidRaster(r: unknown): r is { id: string; tiled?: boolean } {
    return typeof r === "object" && r !== null && "id" in r && typeof r.id === "string"
}

const localRasterState = (raster: { id: string; tiled?: boolean }) => ({
    id: raster.id,
    visible: !!raster.tiled && addLayerVisibilityToEntities([raster])[0].visible,
    isDownloading: false,
})

function isTiledUpdate(updates: unknown): updates is { tiled: true } {
    return (
        typeof updates === "object" &&
        updates !== null &&
        "tiled" in updates &&
        updates.tiled === true
    )
}

function rasterIdInPath(path: unknown) {
    if (!Array.isArray(path) || path.at(-2) !== "PDFRasters") return null
    const target: unknown = path.at(-1)
    return typeof target === "object" && target !== null && "id" in target
        ? (target.id as string)
        : null
}

const upgradeTiledRaster = (rasterById: RasterById, path: unknown, updates: unknown) => {
    if (!isTiledUpdate(updates)) return
    const id = rasterIdInPath(path)
    if (!id || !rasterById[id]) return
    rasterById[id].visible = addLayerVisibilityToEntities([{ id }])[0].visible
}

const {
    reducer: uiStateReducer,
    actions: { mockRasterUploaded },
} = createSlice({
    name: uiStateName,
    initialState,
    reducers: {
        mockRasterUploaded: (state, { payload }: { payload: { id: ID; pdfBlobUrl: string } }) => {
            state.rasterById[payload.id] = {
                id: payload.id,
                pdfBlobUrl: payload.pdfBlobUrl,
                visible: true,
                isDownloading: false,
            }
        },
    },
    extraReducers: (b) =>
        b
            // Populate the local rasters object with a default visibility and downloading status
            // if another client just created a PDFRaster and a socket event was emitted to this client.
            .addCase(addEntitySocket, (state, { payload: { data, path } }) => {
                if (Array.isArray(path) && path.at(-1) === "PDFRasters") {
                    state.rasterById[data.id] ??= localRasterState(data)
                }
            })
            .addCase(updateEntitySocket, (state, { payload: { path, updates } }) => {
                upgradeTiledRaster(state.rasterById, path, updates)
            })
            .addCase(handleIncomingSocketEvents, (state, { payload: { events } }) => {
                events.forEach((event) => {
                    if (
                        event.type === "add" &&
                        Array.isArray(event.path) &&
                        event.path.at(-1) === "PDFRasters" &&
                        isValidRaster(event.data)
                    ) {
                        state.rasterById[event.data.id] ??= localRasterState(event.data)
                    }
                    if (event.type === "update")
                        upgradeTiledRaster(state.rasterById, event.path, event.data)
                })
            })
            /**
             * On project load, take each raster, then define and add their local
             * traits to the map in this slice. Just the url, visibility (false initially)
             * and downloading status (false initially).
             */
            .addCase(loadProject.fulfilled, (state, action) => {
                const rasterById: RasterById = {}
                action.payload.PDFRasters.forEach((raster) => {
                    rasterById[raster.id] = localRasterState(raster)
                })

                state.rasterById = rasterById
            })
            .addCase(updatePDFRasterVisibility.pending, (state, action) => {
                state.rasterById[action.meta.arg.id].isDownloading =
                    action.meta.arg.visible && !pdfRasterImageData[action.meta.arg.id]
                state.rasterById[action.meta.arg.id].visible = action.meta.arg.visible
            })
            .addCase(updatePDFRasterVisibility.fulfilled, (state, action) => {
                const { id } = action.meta.arg

                // If the layer is deleted while it's downloading.
                if (!state.rasterById[id]) return

                state.rasterById[id].isDownloading = false
                if (action.payload.imageData) pdfRasterImageData[id] ??= action.payload.imageData
                if (action.payload.pdfBlobUrl)
                    state.rasterById[id].pdfBlobUrl ??= action.payload.pdfBlobUrl
            })
            .addCase(updatePDFRasterVisibility.rejected, (state, action) => {
                if (!state.rasterById[action.meta.arg.id]) return
                state.rasterById[action.meta.arg.id].isDownloading = false
            }),
})

const { selectLocalRastersMap } = withRoot(
    (r: Record<uiStateNameType, UIState>) => r[uiStateName],
    {
        selectLocalRastersMap: () => (s) => s.rasterById,
    }
)

const selectRoot = (s: RootState) => s[uiStateName] as UIState
export const selectLocalRasterById = memoize((id: string) =>
    createSelector([selectRoot], (root) => root.rasterById[id])
)

export { selectLocalRastersMap, mockRasterUploaded }

export const uiReducer = { [uiStateName]: uiStateReducer }
